import Config from "./Config"
import { promiseTimeout } from "./Util"
import ApiClient, { ApiRoutes } from "../api/ApiClient"
import { route } from "../routes/routes"

/**
 * Loads Stripe.js from Stripe's CDN, mirroring how AuthNet.ts loads Accept.js.
 *
 * Deliberately no npm dependency: Stripe requires their script to be served
 * from js.stripe.com for PCI SAQ-A, so bundling buys nothing, and this keeps
 * yarn.lock untouched on a codebase being retired in a few months.
 */

let stripeScript: HTMLScriptElement
let stripePromise: Promise<any> | null = null

export function isStripeEnabled (): boolean {
  return Config.PAYMENT_PROVIDER === 'Stripe' && !!Config.STRIPE_PUBLISHABLE_KEY
}

/**
 * Member/chapter intents are bound to that owner's Stripe customer, which is
 * what records the cardholder's permission for later off-session charges.
 * The guest flows have no customer yet, so they authenticate without one.
 */
export async function authenticateSavedPaymentMethod (invoiceSlug: string, paymentMethodId: string): Promise<void> {
  let clientSecret

  try {
    const response = await ApiClient.getInstance().post(
      route(ApiRoutes.public.invoice.createSetupIntent, { slug: invoiceSlug }),
      { payment_method: paymentMethodId }
    )

    clientSecret = response.data.client_secret
  } catch (error) {
    return
  }

  if (!clientSecret) {
    return
  }

  let stripe

  try {
    stripe = await loadStripeJs()
  } catch (error) {
    return
  }

  const result = await stripe.confirmCardSetup(clientSecret)

  if (result.error) {
    throw new Error(result.error.message || 'Your bank could not verify this card')
  }
}

export async function fetchSetupIntentClientSecret (memberId?: number, chapterId?: number): Promise<string> {
  const url = memberId
    ? route(ApiRoutes.members.createStripeSetupIntent, { id: memberId })
    : chapterId
      ? route(ApiRoutes.chapters.createStripeSetupIntent, { id: chapterId })
      : route(ApiRoutes.public.stripe.createSetupIntent)

  const response = await ApiClient.getInstance().post(url)

  return response.data.client_secret
}

export async function loadStripeJs (timeout: number = 10000): Promise<any> {
  if (stripePromise) {
    return stripePromise
  }

  stripePromise = new Promise<any>((resolve, reject) => {
    const win = window as any

    if (!Config.STRIPE_PUBLISHABLE_KEY) {
      reject(new Error('Stripe publishable key is not configured'))
      return
    }

    if (typeof win.Stripe !== 'undefined') {
      resolve(win.Stripe(Config.STRIPE_PUBLISHABLE_KEY))
      return
    }

    const loaded = new Promise<any>((innerResolve, innerReject) => {
      stripeScript = document.createElement('script')
      stripeScript.src = 'https://js.stripe.com/v3/'
      stripeScript.onload = () => {
        if (typeof win.Stripe === 'undefined') {
          innerReject(new Error('Stripe.js loaded but window.Stripe is missing'))
          return
        }
        innerResolve(win.Stripe(Config.STRIPE_PUBLISHABLE_KEY))
      }
      stripeScript.onerror = () => innerReject(new Error('Error loading Stripe.js'))
      document.body.appendChild(stripeScript)
    })

    promiseTimeout(timeout, loaded).then(resolve, reject)
  })

  // Allow a retry if loading failed, rather than caching the rejection forever.
  stripePromise.catch(() => { stripePromise = null })

  return stripePromise
}
